// =============================================================================
// SHOPPING BAG CONTEXT
// =============================================================================
// React context for shopping bag state management

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { shoppingBagService } from '@/services/shoppingBag';
import type { ShoppingBag, ShoppingBagItem, ShoppingBagContextValue } from '@/types/shoppingBag';

const ShoppingBagContext = createContext<ShoppingBagContextValue | undefined>(undefined);

interface ShoppingBagProviderProps {
  children: React.ReactNode;
}

export const ShoppingBagProvider: React.FC<ShoppingBagProviderProps> = ({ children }) => {
  const [bag, setBag] = useState<ShoppingBag>(shoppingBagService.getBag());
  const [isOpen, setIsOpen] = useState(false);

  // Subscribe to bag changes
  useEffect(() => {
    const unsubscribe = shoppingBagService.subscribe((updatedBag) => {
      setBag(updatedBag);
    });

    return unsubscribe;
  }, []);

  const addItem = useCallback((itemData: Omit<ShoppingBagItem, 'id' | 'quantity' | 'addedAt'>) => {
    shoppingBagService.addItem(itemData);
  }, []);

  const removeItem = useCallback((itemId: string) => {
    shoppingBagService.removeItem(itemId);
  }, []);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    shoppingBagService.updateQuantity(itemId, quantity);
  }, []);

  const clearBag = useCallback(() => {
    shoppingBagService.clearBag();
  }, []);

  const openBag = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeBag = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleBag = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const value: ShoppingBagContextValue = {
    bag,
    addItem,
    removeItem,
    updateQuantity,
    clearBag,
    isOpen,
    openBag,
    closeBag,
    toggleBag,
  };

  return (
    <ShoppingBagContext.Provider value={value}>
      {children}
    </ShoppingBagContext.Provider>
  );
};

/**
 * Hook to access shopping bag context
 */
export const useShoppingBag = (): ShoppingBagContextValue => {
  const context = useContext(ShoppingBagContext);
  if (context === undefined) {
    throw new Error('useShoppingBag must be used within a ShoppingBagProvider');
  }
  return context;
};

